import { DataTable, Dialog, Loader } from "@/components";
import { Badge, Button } from "@/components/ui";
import { cn } from "@/lib/utils";
import { ResetPassword } from "@/pages";
import { ArrowLeft, KeyRound, Mail, MapPin, Pencil, Phone, Save, ShieldCheck, User, X } from "lucide-react";
import { ReactNode } from "react";
import EditOwner from "./EditOwner/EditOwner";
import useEditOwner, { EDIT_OWNER_FORM_ID } from "./EditOwner/useEditOwner";
import useOwnerDetails, { formatAddress } from "./useOwnerDetails";

const Field = ({ label, value, icon }: { label: string; value?: ReactNode; icon?: ReactNode }) => (
  <div className="flex gap-2 min-w-0">
    {icon ? <span className="mt-0.5 text-gray-400">{icon}</span> : <></>}
    <div className="min-w-0">
      <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{label}</p>
      <p className="text-sm text-gray-900 break-words">{value || "—"}</p>
    </div>
  </div>
);

const OwnerDetails = () => {
  const {
    user, relationships, userClassification, columns, accountSecurity, isLoading, isAdmin, canEdit, isEditing, setIsEditing,
    openResetDialog, setOpenResetDialog, onBack, formatDate, DATE_TIME,
    openTermsDialog, setOpenTermsDialog, termsAcceptanceLogs, latestTermsAcceptance, termsColumns, isTermsLoading, isTermsError,
  } = useOwnerDetails();
  const { form, onSubmit, isSaving, stateOptions } = useEditOwner({
    user,
    onDone: () => setIsEditing(false),
  });
  const onCancelEdit = () => {
    form.reset();
    setIsEditing(false);
  };

  if (isLoading) return <Loader />;

  const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(" ");
  const correspondence = formatAddress(user?.correspondenceAddress);

  return (
    <div className="p-4 md:p-6 flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="outline" size="icon" onClick={onBack} aria-label="Back">
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h2 className="text-lg md:text-xl font-semibold">User Master Data</h2>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isEditing ? (
            <>
              <Button variant="outline" onClick={onCancelEdit} disabled={isSaving}>
                <X className="w-4 h-4" /> Cancel
              </Button>
              <Button type="submit" form={EDIT_OWNER_FORM_ID} disabled={isSaving}>
                <Save className="w-4 h-4" /> Save Changes
              </Button>
            </>
          ) : canEdit ? (
            <Button onClick={() => setIsEditing(true)}>
              <Pencil className="w-4 h-4" /> Edit Owner
            </Button>
          ) : <></>}
          {isAdmin ? (
            <Button variant="outline" onClick={() => setOpenResetDialog(true)}>
              <KeyRound className="w-4 h-4" /> Reset Password
            </Button>
          ) : <></>}
        </div>
      </div>

      <div className="bg-white rounded-xl border-t-4 border-primary shadow-sm p-4 md:p-6 flex flex-col gap-6">
        <div className="flex flex-col lg:flex-row gap-6">
          <div className="flex items-start gap-4 lg:pr-6 lg:border-r">
            <div className="w-14 h-14 rounded-xl bg-blue-50 flex items-center justify-center text-primary shrink-0">
              <User className="w-7 h-7" />
            </div>
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 className="text-xl font-semibold">{fullName || "—"}</h3>
                <Badge
                  variant="outline"
                  className={cn(
                    user?.active
                      ? "border-green-200 bg-green-50 text-green-700"
                      : "border-red-200 bg-red-50 text-red-600",
                    "rounded-md px-2 py-0.5"
                  )}
                >
                  {user?.active ? "Active" : "Inactive"}
                </Badge>
              </div>
              <p className="text-sm text-gray-500">
                User ID {user?.userId} · User Name {user?.userName}
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 flex-1">
            <Field label="Login ID" value={user?.loginId} />
            <Field label="User Classification" value={userClassification} />
            <Field label="Email Verification" value={user?.emailVerified ? "Verified" : "Not verified"} />
            <Field label="Account Security" value={accountSecurity} />
          </div>
        </div>

        {isEditing ? (
          <EditOwner form={form} onSubmit={onSubmit} stateOptions={stateOptions} />
        ) : (
        <div className="bg-gray-50 rounded-lg p-4 grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-5">
          <Field label="Title" value={user?.title} />
          <Field label="Primary Email" value={user?.email} icon={<Mail className="w-4 h-4" />} />
          <Field label="Secondary Email" value={user?.email2} icon={<Mail className="w-4 h-4" />} />
          <Field label="Mobile" value={user?.phoneNumber} icon={<Phone className="w-4 h-4" />} />
          <Field label="Landline" value={user?.landlineNumber} icon={<Phone className="w-4 h-4" />} />
          <div className="sm:col-span-2 xl:col-span-3">
            <Field label="Primary Contact Address" value={formatAddress(user?.address)} icon={<MapPin className="w-4 h-4" />} />
          </div>
          <div className="sm:col-span-2 xl:col-span-4">
            <Field
              label="Correspondence Address"
              value={correspondence || "Same as primary / not separately maintained"}
              icon={<MapPin className="w-4 h-4" />}
            />
          </div>
        </div>
        )}

        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 border-t pt-4">
          <Field label="Password Expires" value={formatDate(user?.passwordExpiry, DATE_TIME)} />
          <Field label="Terms Accepted" value={
            <Button type="button" variant="link" className="h-auto p-0 text-sm" onClick={() => setOpenTermsDialog(true)}>
              {latestTermsAcceptance ? formatDate(latestTermsAcceptance.acceptedAt) : "Not recorded"}
            </Button>
          } />
          <Field label="Created" value={formatDate(user?.createdAt, DATE_TIME)} />
          <Field label="Last Updated" value={formatDate(user?.updatedAt, DATE_TIME)} />
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 flex flex-col gap-4">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <div>
            <h3 className="text-lg font-semibold flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-primary" /> Property relationships
            </h3>
            <p className="text-sm text-gray-500">
              Read-only report of every title, tenancy, and management relationship linked to this person.
              Title changes remain controlled by conversion/OCP.
            </p>
          </div>
          <Badge variant="outline" className="rounded-md px-2 py-1">
            {relationships.length} relationship{relationships.length === 1 ? "" : "s"}
          </Badge>
        </div>
        <DataTable data={relationships} columns={columns} />
      </div>

      <ResetPassword open={openResetDialog} setOpen={setOpenResetDialog} selectedUser={user} />
      <Dialog
        open={openTermsDialog}
        setOpen={setOpenTermsDialog}
        title="Terms Acceptance History"
        contentClassName="max-w-[95vw] lg:max-w-[1100px]"
        hideFooter
      >
        {isTermsLoading ? <Loader /> : isTermsError ? (
          <p className="text-sm text-red-600">Unable to load terms acceptance history.</p>
        ) : (
          <DataTable data={termsAcceptanceLogs} columns={termsColumns} />
        )}
      </Dialog>
    </div>
  );
};

export default OwnerDetails;
